import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EventFormDialog } from "@/components/EventFormDialog";
import { useMe } from "@/components/StaffGate";
import { fetchEvents, useAuthedApi, errMsg } from "@/lib/api";
import { BRANCHES, STATUS, fmtDate } from "@/lib/format";
import { BranchTabs } from "@/components/BranchTabs";

export default function DashboardPage() {
  const me = useMe();
  const api = useAuthedApi();
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [branch, setBranch] = useState("all");
  const shown = events.filter((e) => branch === "all" || e.branch === branch);

  useEffect(() => {
    fetchEvents().then(setEvents).catch(() => toast.error("Não foi possível carregar os eventos"));
  }, []);

  const upsert = (ev) => setEvents((p) => (p.some((x) => x.id === ev.id) ? p.map((x) => (x.id === ev.id ? ev : x)) : [...p, ev]).sort((a, b) => a.setup_date.localeCompare(b.setup_date)));

  const changeStatus = async (ev, status) => {
    try { upsert(await api("patch", `/events/${ev.id}/status`, { status })); toast.success(`Status: ${STATUS[status].label}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try { await api("delete", `/events/${toDelete.id}`); setEvents((p) => p.filter((x) => x.id !== toDelete.id)); toast.success("Evento excluído"); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <main data-testid="dashboard-page" className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400">Painel · {me.role === "admin" ? "Administrador" : "Editor"}</p>
          <h1 className="font-display text-4xl font-extrabold uppercase sm:text-5xl">Gestão de eventos</h1>
        </div>
        <div className="flex gap-2">
          {me.role === "admin" && <Link to="/painel/usuarios" data-testid="manage-users-link" className="inline-flex items-center gap-2 rounded-sm border border-slate-700 px-4 py-2 text-sm transition-colors hover:border-amber-500"><Users size={16} />Usuários</Link>}
          <button data-testid="create-event-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-2 rounded-sm bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-400"><Plus size={16} />Novo evento</button>
        </div>
      </div>

      <BranchTabs value={branch} onChange={setBranch} events={events} />

      <div className="overflow-x-auto rounded-md border border-slate-800 bg-slate-900/60">
        <table data-testid="dashboard-events-table" className="w-full min-w-[760px] text-sm">
          <thead className="border-b border-slate-800 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="p-3">Evento</th><th className="p-3">Filial</th><th className="p-3">Montagem</th><th className="p-3">Data</th><th className="p-3">Desmontagem</th><th className="p-3">Status</th><th className="p-3 text-right">Ações</th></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="dashboard-empty">Nenhum evento cadastrado.</td></tr>}
            {shown.map((ev) => (
              <tr key={ev.id} data-testid={`dashboard-row-${ev.id}`} className="border-b border-slate-800/60 transition-colors hover:bg-slate-800/40">
                <td className="p-3"><div className="font-medium">{ev.title}</div><div className="text-xs text-slate-500">{ev.location}</div></td>
                <td className="p-3 text-xs" data-testid={`dashboard-branch-${ev.id}`}>{BRANCHES[ev.branch]}</td>
                <td className="p-3 font-mono text-xs">{fmtDate(ev.setup_date, "dd/MM")} {ev.setup_time}</td>
                <td className="p-3 font-mono text-xs text-amber-400">{fmtDate(ev.event_date, "dd/MM/yy")} {ev.event_time}</td>
                <td className="p-3 font-mono text-xs">{fmtDate(ev.teardown_date, "dd/MM")} {ev.teardown_time}</td>
                <td className="p-3">
                  <select data-testid={`status-select-${ev.id}`} value={ev.status} onChange={(e) => changeStatus(ev, e.target.value)} className={`rounded-sm border bg-transparent px-2 py-1 font-mono text-[11px] uppercase ${STATUS[ev.status].cls}`}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-slate-900 text-slate-100">{v.label}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <Link to={`/evento/${ev.id}`} data-testid={`view-event-button-${ev.id}`} className="rounded-sm p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><Eye size={16} /></Link>
                    <button data-testid={`edit-event-button-${ev.id}`} onClick={() => { setEditing(ev); setFormOpen(true); }} className="rounded-sm p-2 text-slate-400 hover:bg-slate-800 hover:text-amber-400"><Pencil size={16} /></button>
                    <button data-testid={`delete-event-button-${ev.id}`} onClick={() => setToDelete(ev)} className="rounded-sm p-2 text-slate-400 hover:bg-slate-800 hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EventFormDialog open={formOpen} onOpenChange={setFormOpen} event={editing} onSaved={upsert} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog" className="border-slate-800 bg-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir evento?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" será removido da agenda pública.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={remove} className="bg-red-600 hover:bg-red-500">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
